import React, { useEffect, useRef, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { Spinner } from 'react-bootstrap';
import { optionSellingAPI, OptionSellingMarket } from '../services/optionSellingApi';
import { brokerAPI, BrokerStatus, BrokerFeatureToggle } from '../services/brokerApi';

const SELL_OFFSET_OPTIONS = [
  { value: -1, label: 'ATM-1' },
  { value: 0, label: 'ATM' },
  { value: 1, label: 'ATM+1' },
];
// Target hedge premium (₹), every rupee from the market's floor up to 40 — Sensex options
// don't get as cheap as Nifty's, so its floor is higher. The backend finds whichever OTM
// strike's current LTP is closest to the chosen value.
const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
const HEDGE_DISTANCE_RUPEE_OPTIONS: Record<OptionSellingMarket, number[]> = {
  NIFTY: range(4, 40),
  SENSEX: range(15, 40),
};
const ACCENT: Record<OptionSellingMarket, { border: string; bg: string; text: string }> = {
  NIFTY: { border: '#90caf9', bg: '#e3f2fd', text: '#0d6efd' },
  SENSEX: { border: '#ffcc80', bg: '#fff3e0', text: '#fd7e14' },
};

interface PanelFormState {
  expiryDate: string;
  sellOffset: number;
  hedgeDistanceRupees: number;
  // Client's explicit ask: replaces the old flat Lots field — quantity is now sized from this %
  // of the configured Capital Amount (Broker Management) against the SELL CE leg's LTP, same
  // formula Nifty Buying already uses. Applied to both SELL legs (CE+PE) and their hedges —
  // same "one lots number everywhere" shape the old field had.
  capitalPercentage: number;
  slippagePercentage: number;
  delaySeconds: number;
  // When true, the position is auto-exited once the X/Y breadth condition below is hit (same
  // mechanism NSE/BSE and Spread SL already use). When false, these SL values are reference
  // only and nothing happens automatically.
  slEnabled: boolean;
  // SL: two condition pairs joined by OR (pair1: X1/Y1, pair2: X2/Y2), each value itself
  // shown as two boxes (integer + decimal) per the client's hand-drawn design.
  // Typed as `number | ''` (not just `number`) so the input can sit empty while the user is
  // backspacing/retyping — binding these directly to `number` with `value={0}` made every field
  // snap back to "0" instantly on backspace (Number('') is 0, not NaN, so the controlled value
  // never actually became empty), making it impossible to clear a field and type a fresh value.
  slX1a: number | ''; slX1b: number | '';
  slY1a: number | ''; slY1b: number | '';
  slX2a: number | ''; slX2b: number | '';
  slY2a: number | ''; slY2b: number | '';
  slAmount: number | '';
  targetPctFromPeak: number | '';
  broker: string;
}

const defaultForm = (market: OptionSellingMarket): PanelFormState => ({
  expiryDate: '',
  sellOffset: 0,
  hedgeDistanceRupees: HEDGE_DISTANCE_RUPEE_OPTIONS[market][0],
  capitalPercentage: 1,
  slippagePercentage: 3,
  delaySeconds: 3,
  slEnabled: false,
  slX1a: 0, slX1b: 0,
  slY1a: 0, slY1b: 0,
  slX2a: 0, slX2b: 0,
  slY2a: 0, slY2b: 0,
  slAmount: 0,
  targetPctFromPeak: 0,
  broker: 'DHAN',
});

// A field left transiently empty (mid-backspace/retype) is stored as '' in form state so the
// input can actually go blank — sanitize back to a real number wherever it leaves this page
// (place order, auto-save), same convention both call sites need.
const num = (v: number | ''): number => (v === '' ? 0 : v);

// Auto-save (debounced, on every form change) sends the whole form object — sanitize the same
// transiently-empty SL fields before it goes out, same as placeForMarket does.
const sanitizeForSave = (form: PanelFormState) => ({
  ...form,
  slX1a: num(form.slX1a), slX1b: num(form.slX1b),
  slY1a: num(form.slY1a), slY1b: num(form.slY1b),
  slX2a: num(form.slX2a), slX2b: num(form.slX2b),
  slY2a: num(form.slY2a), slY2b: num(form.slY2b),
  slAmount: num(form.slAmount), targetPctFromPeak: num(form.targetPctFromPeak),
});

// Compact section/input style tokens — matched to SpreadConfigForm.tsx so this page reads as
// the same design system as the rest of the app instead of the much larger react-bootstrap
// Container/Row/Col defaults.
const sectionStyle = (borderColor: string, bg: string): React.CSSProperties => ({
  border: `1px solid ${borderColor}`,
  background: bg,
  borderRadius: '6px',
  padding: '12px',
  marginBottom: '12px',
  width: 'fit-content',
});
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 500, color: '#374151', marginBottom: '4px' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' };
const readOnlyInputStyle: React.CSSProperties = { ...inputStyle, background: '#f3f4f6', color: '#6b7280' };
const sectionHeadingStyle: React.CSSProperties = { fontSize: '13px', fontWeight: 600, margin: '0 0 12px 0' };

interface PanelProps {
  market: OptionSellingMarket;
  form: PanelFormState;
  onChange: (field: keyof PanelFormState, value: number | string | boolean) => void;
  placing: boolean;
  onPlace: () => void;
  exiting: boolean;
  onExit: () => void;
}

const MarketPanel: React.FC<PanelProps> = ({ market, form, onChange, placing, onPlace, exiting, onExit }) => {
  const accent = ACCENT[market];
  const [brokers, setBrokers] = useState<BrokerStatus[]>([]);
  const [featureToggles, setFeatureToggles] = useState<BrokerFeatureToggle[]>([]);
  const isBrokerEnabledFor = (broker: string) =>
    featureToggles.find(t => t.broker === broker && t.feature === 'OPTION_SELLING')?.is_enabled ?? (broker === 'DHAN');
  useEffect(() => {
    brokerAPI.list().then(res => setBrokers(res.data.filter(b => b.implemented))).catch(() => {});
    brokerAPI.listFeatures().then(res => setFeatureToggles(res.data)).catch(() => {});
  }, []);

  return (
    <div style={{ border: `1px solid ${accent.border}`, borderRadius: '8px', padding: '14px', width: 'fit-content' }}>
      <h4 style={{ fontSize: '14px', fontWeight: 700, color: accent.text, margin: '0 0 12px 0' }}>{market}</h4>

      <div style={sectionStyle('#e5e7eb', '#f9fafb')}>
        <div style={{ display: 'grid', gridTemplateColumns: '150px 100px 130px 120px 100px 120px', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Expiry Date</label>
            <input type="date" style={inputStyle} value={form.expiryDate} onChange={e => onChange('expiryDate', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Sell Strike</label>
            <select style={inputStyle} value={form.sellOffset} onChange={e => onChange('sellOffset', Number(e.target.value))}>
              {SELL_OFFSET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Hedge Distance (₹ premium)</label>
            <select style={inputStyle} value={form.hedgeDistanceRupees} onChange={e => onChange('hedgeDistanceRupees', Number(e.target.value))}>
              {HEDGE_DISTANCE_RUPEE_OPTIONS[market].map(o => <option key={o} value={o}>₹{o}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Slippage (%)</label>
            <input type="number" step="0.1" style={inputStyle} value={form.slippagePercentage} onChange={e => onChange('slippagePercentage', Number(e.target.value))} />
          </div>
          <div>
            <label style={labelStyle}>Delay (s)</label>
            <input type="number" style={inputStyle} value={form.delaySeconds} onChange={e => onChange('delaySeconds', Number(e.target.value))} />
          </div>
          <div>
            <label style={labelStyle}>Broker</label>
            <select style={inputStyle} value={form.broker} onChange={e => onChange('broker', e.target.value)}>
              <option value="DHAN">Dhan</option>
              {brokers.filter(b => b.broker !== 'DHAN').map(b => (
                <option key={b.broker} value={b.broker}>{b.broker.replace('_', ' ')}{!isBrokerEnabledFor(b.broker) ? ' (disabled)' : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <div style={sectionStyle('#fecaca', '#fef2f2')}>
          <h3 style={{ ...sectionHeadingStyle, color: '#991b1b' }}>SELL (CE + PE strangle)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 130px', gap: '12px' }}>
            <div>
              <label style={labelStyle}>% Cap</label>
              <input type="number" min={0.01} step={0.01} style={inputStyle} value={form.capitalPercentage} onChange={e => onChange('capitalPercentage', Number(e.target.value))} />
            </div>
            <div>
              <label style={labelStyle}>Qty (Q)</label>
              <input type="text" style={readOnlyInputStyle} value="Auto (at placement)" disabled />
            </div>
          </div>
        </div>
        <div style={sectionStyle('#a7f3d0', '#ecfdf5')}>
          <h3 style={{ ...sectionHeadingStyle, color: '#065f46' }}>BUY (hedge, placed first)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 100px', gap: '12px' }}>
            <div>
              <label style={labelStyle}>% Cap</label>
              <input type="text" style={readOnlyInputStyle} value={`${form.capitalPercentage}% (same as SELL)`} disabled />
            </div>
            <div>
              <label style={labelStyle}>Qty (Q)</label>
              <input type="text" style={readOnlyInputStyle} value="Auto (at placement)" disabled />
            </div>
          </div>
          <div style={{ fontSize: '10px', color: '#065f46', marginTop: '6px' }}>Same as SELL — quantities always match</div>
        </div>
      </div>

      <div style={sectionStyle('#fde68a', '#fffbeb')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <h3 style={{ ...sectionHeadingStyle, margin: 0, color: '#92400e' }}>Stop Loss</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: '#92400e', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.slEnabled} onChange={e => onChange('slEnabled', e.target.checked)} />
            SL {form.slEnabled ? 'ON — auto-exits on condition' : 'OFF — reference only'}
          </label>
        </div>
        {/* Row 1: first X/Y pair */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '12px' }}>
          <div>
            <label style={labelStyle}>X</label>
            <div style={{ display: 'flex', gap: '3px', marginBottom: '2px' }}>
              <div style={{ width: '68px', fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>X</div>
              <div style={{ width: '68px', fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>X%</div>
            </div>
            <div style={{ display: 'flex', gap: '3px' }}>
              <input type="number" step="1" title="X" style={{ ...inputStyle, width: '68px' }} value={form.slX1a} onChange={e => onChange('slX1a', e.target.value === '' ? '' : Math.round(Number(e.target.value)))} />
              <input type="number" step="0.01" title="X%" style={{ ...inputStyle, width: '68px' }} value={form.slX1b} onChange={e => onChange('slX1b', e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Y</label>
            <div style={{ display: 'flex', gap: '3px', marginBottom: '2px' }}>
              <div style={{ width: '68px', fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>Y</div>
              <div style={{ width: '68px', fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>Y%</div>
            </div>
            <div style={{ display: 'flex', gap: '3px' }}>
              <input type="number" step="1" title="Y" style={{ ...inputStyle, width: '68px' }} value={form.slY1a} onChange={e => onChange('slY1a', e.target.value === '' ? '' : Math.round(Number(e.target.value)))} />
              <input type="number" step="0.01" title="Y%" style={{ ...inputStyle, width: '68px' }} value={form.slY1b} onChange={e => onChange('slY1b', e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>
        </div>
        {/* Row 2: OR label + second X/Y pair (the OR alternative) */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '12px' }}>
          <div style={{ paddingBottom: '8px', fontSize: '12px', fontWeight: 700, color: '#92400e' }}>OR</div>
          <div>
            <label style={labelStyle}>X</label>
            <div style={{ display: 'flex', gap: '3px', marginBottom: '2px' }}>
              <div style={{ width: '68px', fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>X</div>
              <div style={{ width: '68px', fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>X%</div>
            </div>
            <div style={{ display: 'flex', gap: '3px' }}>
              <input type="number" step="1" title="X" style={{ ...inputStyle, width: '68px' }} value={form.slX2a} onChange={e => onChange('slX2a', e.target.value === '' ? '' : Math.round(Number(e.target.value)))} />
              <input type="number" step="0.01" title="X%" style={{ ...inputStyle, width: '68px' }} value={form.slX2b} onChange={e => onChange('slX2b', e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Y</label>
            <div style={{ display: 'flex', gap: '3px', marginBottom: '2px' }}>
              <div style={{ width: '68px', fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>Y</div>
              <div style={{ width: '68px', fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>Y%</div>
            </div>
            <div style={{ display: 'flex', gap: '3px' }}>
              <input type="number" step="1" title="Y" style={{ ...inputStyle, width: '68px' }} value={form.slY2a} onChange={e => onChange('slY2a', e.target.value === '' ? '' : Math.round(Number(e.target.value)))} />
              <input type="number" step="0.01" title="Y%" style={{ ...inputStyle, width: '68px' }} value={form.slY2b} onChange={e => onChange('slY2b', e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>
        </div>
        {/* Row 3: SL Amount + Target */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>SL Amount (₹)</label>
            <input type="number" style={{ ...inputStyle, width: '110px' }} value={form.slAmount} onChange={e => onChange('slAmount', e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
          <div>
            <label style={labelStyle}>Target Amount (₹) from Peak</label>
            <input type="number" step="0.01" style={{ ...inputStyle, width: '130px' }} value={form.targetPctFromPeak} onChange={e => onChange('targetPctFromPeak', e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={onPlace}
          disabled={placing}
          style={{
            padding: '8px 20px', fontSize: '13px', fontWeight: 600, color: 'white',
            background: placing ? '#f3a6a6' : '#dc3545', border: 'none', borderRadius: '4px',
            cursor: placing ? 'default' : 'pointer',
          }}
        >
          {placing ? <Spinner size="sm" animation="border" /> : `Place Order (${market})`}
        </button>
        <button
          onClick={onExit}
          disabled={exiting}
          style={{
            padding: '8px 20px', fontSize: '13px', fontWeight: 600, color: '#374151',
            background: 'white', border: '1px solid #9ca3af', borderRadius: '4px',
            cursor: exiting ? 'default' : 'pointer',
          }}
        >
          {exiting ? <Spinner size="sm" animation="border" /> : `Exit (${market})`}
        </button>
      </div>
    </div>
  );
};

const OptionSellingPage: React.FC = () => {
  const [niftyForm, setNiftyForm] = useState<PanelFormState>(defaultForm('NIFTY'));
  const [sensexForm, setSensexForm] = useState<PanelFormState>(defaultForm('SENSEX'));
  const [placingNifty, setPlacingNifty] = useState(false);
  const [placingSensex, setPlacingSensex] = useState(false);
  const [placingBoth, setPlacingBoth] = useState(false);
  const [exitingNifty, setExitingNifty] = useState(false);
  const [exitingSensex, setExitingSensex] = useState(false);
  const [exitingBoth, setExitingBoth] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Remember the last-used screen values per market — loaded once on mount, then auto-saved
  // (debounced) on every change so each panel repopulates on the next visit.
  const loadedRef = useRef(false);
  const niftySaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sensexSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    optionSellingAPI.getSettings().then(res => {
      const saved = res.data;
      if (saved?.NIFTY) {
        const s = saved.NIFTY;
        setNiftyForm({
          expiryDate: s.expiryDate || '', sellOffset: s.sellOffset, hedgeDistanceRupees: s.hedgeDistanceRupees,
          capitalPercentage: s.capitalPercentage, slippagePercentage: s.slippagePercentage, delaySeconds: s.delaySeconds,
          slEnabled: !!s.slEnabled,
          slX1a: s.slX1a, slX1b: s.slX1b, slY1a: s.slY1a, slY1b: s.slY1b,
          slX2a: s.slX2a, slX2b: s.slX2b, slY2a: s.slY2a, slY2b: s.slY2b,
          slAmount: s.slAmount, targetPctFromPeak: s.targetPctFromPeak,
          broker: s.broker || 'DHAN',
        });
      }
      if (saved?.SENSEX) {
        const s = saved.SENSEX;
        setSensexForm({
          expiryDate: s.expiryDate || '', sellOffset: s.sellOffset, hedgeDistanceRupees: s.hedgeDistanceRupees,
          capitalPercentage: s.capitalPercentage, slippagePercentage: s.slippagePercentage, delaySeconds: s.delaySeconds,
          slEnabled: !!s.slEnabled,
          slX1a: s.slX1a, slX1b: s.slX1b, slY1a: s.slY1a, slY1b: s.slY1b,
          slX2a: s.slX2a, slX2b: s.slX2b, slY2a: s.slY2a, slY2b: s.slY2b,
          slAmount: s.slAmount, targetPctFromPeak: s.targetPctFromPeak,
          broker: s.broker || 'DHAN',
        });
      }
      loadedRef.current = true;
    }).catch(err => {
      console.error('[OptionSelling] Failed to load saved settings:', err.message);
      loadedRef.current = true;
    });
  }, []);

  useEffect(() => {
    if (!loadedRef.current) return;
    if (niftySaveTimerRef.current) clearTimeout(niftySaveTimerRef.current);
    niftySaveTimerRef.current = setTimeout(() => {
      optionSellingAPI.saveSettings('NIFTY', sanitizeForSave(niftyForm)).catch(err =>
        console.error('[OptionSelling] Failed to save Nifty settings:', err.message)
      );
    }, 600);
    return () => { if (niftySaveTimerRef.current) clearTimeout(niftySaveTimerRef.current); };
  }, [niftyForm]);

  useEffect(() => {
    if (!loadedRef.current) return;
    if (sensexSaveTimerRef.current) clearTimeout(sensexSaveTimerRef.current);
    sensexSaveTimerRef.current = setTimeout(() => {
      optionSellingAPI.saveSettings('SENSEX', sanitizeForSave(sensexForm)).catch(err =>
        console.error('[OptionSelling] Failed to save Sensex settings:', err.message)
      );
    }, 600);
    return () => { if (sensexSaveTimerRef.current) clearTimeout(sensexSaveTimerRef.current); };
  }, [sensexForm]);

  const handleNiftyChange = (field: keyof PanelFormState, value: number | string | boolean) => {
    setNiftyForm(prev => ({ ...prev, [field]: value }));
  };
  const handleSensexChange = (field: keyof PanelFormState, value: number | string | boolean) => {
    setSensexForm(prev => ({ ...prev, [field]: value }));
  };

  const placeForMarket = (market: OptionSellingMarket, form: PanelFormState) => {
    return optionSellingAPI.placeOrder(market, {
      expiryDate: form.expiryDate,
      sellOffset: form.sellOffset,
      hedgeDistanceRupees: form.hedgeDistanceRupees,
      capitalPercentage: form.capitalPercentage,
      slippagePercentage: form.slippagePercentage,
      delaySeconds: form.delaySeconds,
      // First box of each pair (whole number) is the count threshold; second box (%) is an
      // OR-alternative checked against the live X%/Y% average-move metric (same one the top-bar
      // X%/X/% widget shows) — confirmed with the client, matches checkAndAutoExitSL's pair1Hit/
      // pair2Hit logic. num() covers a field left transiently empty (backspaced mid-edit).
      slX: num(form.slX1a), slY: num(form.slY1a), slX2: num(form.slX2a), slY2: num(form.slY2a),
      slXPct: num(form.slX1b), slYPct: num(form.slY1b), slX2Pct: num(form.slX2b), slY2Pct: num(form.slY2b),
      slAmount: num(form.slAmount), targetPctFromPeak: num(form.targetPctFromPeak),
      slEnabled: form.slEnabled,
      broker: form.broker,
    });
  };

  const validate = (form: PanelFormState, market: string): string | null => {
    if (!form.expiryDate) return `Please select an expiry date for ${market}`;
    return null;
  };

  const handlePlaceNifty = async () => {
    const err = validate(niftyForm, 'Nifty');
    if (err) { setMessage({ type: 'error', text: err }); return; }
    setPlacingNifty(true);
    setMessage(null);
    try {
      await placeForMarket('NIFTY', niftyForm);
      setMessage({ type: 'success', text: 'Nifty strangle order placed' });
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setPlacingNifty(false);
    }
  };

  const handlePlaceSensex = async () => {
    const err = validate(sensexForm, 'Sensex');
    if (err) { setMessage({ type: 'error', text: err }); return; }
    setPlacingSensex(true);
    setMessage(null);
    try {
      await placeForMarket('SENSEX', sensexForm);
      setMessage({ type: 'success', text: 'Sensex strangle order placed' });
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setPlacingSensex(false);
    }
  };

  const handlePlaceBoth = async () => {
    const niftyErr = validate(niftyForm, 'Nifty');
    const sensexErr = validate(sensexForm, 'Sensex');
    if (niftyErr || sensexErr) {
      setMessage({ type: 'error', text: [niftyErr, sensexErr].filter(Boolean).join(' / ') });
      return;
    }
    setPlacingBoth(true);
    setMessage(null);
    try {
      const results = await Promise.allSettled([
        placeForMarket('NIFTY', niftyForm),
        placeForMarket('SENSEX', sensexForm),
      ]);
      const failed = results.filter(r => r.status === 'rejected') as PromiseRejectedResult[];
      if (failed.length === 0) {
        setMessage({ type: 'success', text: 'Strangle order placed for Nifty & Sensex' });
      } else {
        const errText = failed.map(f => f.reason?.response?.data?.error || f.reason?.message).join('; ');
        setMessage({ type: 'error', text: `${2 - failed.length}/2 placed. Errors: ${errText}` });
      }
    } finally {
      setPlacingBoth(false);
    }
  };

  const handleExitNifty = async () => {
    setExitingNifty(true);
    setMessage(null);
    try {
      const res = await optionSellingAPI.exitAll('NIFTY');
      setMessage({ type: 'success', text: res.data.message });
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setExitingNifty(false);
    }
  };

  const handleExitSensex = async () => {
    setExitingSensex(true);
    setMessage(null);
    try {
      const res = await optionSellingAPI.exitAll('SENSEX');
      setMessage({ type: 'success', text: res.data.message });
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setExitingSensex(false);
    }
  };

  const handleExitBoth = async () => {
    setExitingBoth(true);
    setMessage(null);
    try {
      const results = await Promise.allSettled([optionSellingAPI.exitAll('NIFTY'), optionSellingAPI.exitAll('SENSEX')]);
      const failed = results.filter(r => r.status === 'rejected') as PromiseRejectedResult[];
      if (failed.length === 0) {
        setMessage({ type: 'success', text: 'Exit executed for Nifty & Sensex' });
      } else {
        const errText = failed.map(f => f.reason?.response?.data?.error || f.reason?.message).join('; ');
        setMessage({ type: 'error', text: `${2 - failed.length}/2 exited. Errors: ${errText}` });
      }
    } finally {
      setExitingBoth(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '20px', marginBottom: '14px' }}>
          <h5 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Option Selling (Manual)</h5>
          <button
            onClick={handlePlaceBoth}
            disabled={placingBoth}
            style={{
              padding: '10px 28px', fontSize: '14px', fontWeight: 700, color: 'white',
              background: placingBoth ? '#a78bda' : '#6f42c1', border: 'none', borderRadius: '4px',
              cursor: placingBoth ? 'default' : 'pointer',
            }}
          >
            {placingBoth ? <Spinner size="sm" animation="border" /> : 'Place Order (Both — Nifty + Sensex together)'}
          </button>
          <button
            onClick={handleExitBoth}
            disabled={exitingBoth}
            style={{
              padding: '10px 28px', fontSize: '14px', fontWeight: 700, color: '#374151',
              background: 'white', border: '1px solid #9ca3af', borderRadius: '4px',
              cursor: exitingBoth ? 'default' : 'pointer',
            }}
          >
            {exitingBoth ? <Spinner size="sm" animation="border" /> : 'Exit (Both — Nifty + Sensex)'}
          </button>
        </div>

        {message && (
          <div style={{
            padding: '8px 12px', borderRadius: '4px', marginBottom: '12px', fontSize: '13px',
            background: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
            color: message.type === 'success' ? '#065f46' : '#991b1b',
            border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
          }}>
            {message.text}
            <button
              onClick={() => setMessage(null)}
              style={{ float: 'right', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', color: 'inherit' }}
            >×</button>
          </div>
        )}

        {/* Nifty always left, Sensex always right — horizontal scroll (not wrap) so the two
            panels never stack vertically on a narrower window. */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: '4px' }}>
          <MarketPanel market="NIFTY" form={niftyForm} onChange={handleNiftyChange} placing={placingNifty} onPlace={handlePlaceNifty} exiting={exitingNifty} onExit={handleExitNifty} />
          <MarketPanel market="SENSEX" form={sensexForm} onChange={handleSensexChange} placing={placingSensex} onPlace={handlePlaceSensex} exiting={exitingSensex} onExit={handleExitSensex} />
        </div>
      </div>
    </MainLayout>
  );
};

export default OptionSellingPage;
